import { ApiError } from '@/lib/api';
import { useAuth } from '@/stores/auth';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Acceso a SynQ. El diseno no traia pantalla de login, asi que se construye con
 * su mismo lenguaje: el panel indigo de la barra lateral a la izquierda y el
 * lienzo calido con el formulario a la derecha.
 */
export function LoginPage() {
  const login = useAuth((state) => state.login);
  const endedReason = useAuth((state) => state.endedReason);
  const dismissEndedReason = useAuth((state) => state.dismissEndedReason);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const locked = error?.code === 'ACCOUNT_LOCKED' || error?.code === 'IP_THROTTLED';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    dismissEndedReason();

    try {
      const user = await login(email, password);
      navigate(user.mustChangePassword ? '/cambiar-contrasena' : '/tablero', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError(0, 'No se pudo conectar con el servidor'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen bg-canvas">
      <aside className="hidden w-[45%] max-w-[560px] flex-col justify-between bg-nav p-12 text-nav-text lg:flex">
        <div className="flex items-center gap-2.5">
          <div
            aria-hidden="true"
            className="flex size-[30px] items-center justify-center rounded-[9px] font-display text-[15px] font-bold text-white"
            style={{ background: 'linear-gradient(135deg,#14b8a6,#22d3ee)' }}
          >
            S
          </div>
          <span className="font-display text-base font-bold tracking-[-.01em]">SynQ</span>
        </div>

        <div>
          <h2 className="max-w-md font-display text-[32px] font-semibold leading-[1.2] tracking-[-.02em] text-white text-pretty">
            El backlog, el sprint y el equipo en un mismo sitio.
          </h2>
          <p className="mt-4 max-w-sm text-[14px] leading-relaxed text-nav-muted text-pretty">
            Tablero, backlog priorizado y chat conectados, para que nadie tenga que preguntar en que
            va cada cosa.
          </p>
        </div>

        <p className="text-xs text-nav-label">
          Las cuentas las crea el administrador de tu organizacion.
        </p>
      </aside>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-[360px]">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div
              aria-hidden="true"
              className="flex size-[30px] items-center justify-center rounded-[9px] font-display text-[15px] font-bold text-white"
              style={{ background: 'linear-gradient(135deg,#14b8a6,#22d3ee)' }}
            >
              S
            </div>
            <span className="font-display text-base font-bold tracking-[-.01em]">SynQ</span>
          </div>

          <h1 className="font-display text-[22px] font-semibold tracking-[-.015em]">
            Entra a tu espacio
          </h1>
          <p className="mt-1 text-[13.5px] text-subtle">Usa el correo que te dio el administrador.</p>

          {endedReason && (
            <p
              role="status"
              className="mt-5 rounded-control border border-warn-line bg-warn-soft px-3 py-2 text-[13px] text-warn-text"
            >
              {endedReason}
            </p>
          )}

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4" noValidate>
            <div>
              <label htmlFor="email" className="mb-1.5 block text-[12.5px] font-semibold">
                Correo
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-control border border-line-strong bg-surface px-3 py-2.5 text-[13.5px] outline-none transition-colors focus:border-brand"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1.5 block text-[12.5px] font-semibold">
                Contrasena
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-control border border-line-strong bg-surface px-3 py-2.5 pr-16 text-[13.5px] outline-none transition-colors focus:border-brand"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute inset-y-0 right-0 px-3 text-[12px] font-semibold text-subtle transition-colors hover:text-ink"
                >
                  {showPassword ? 'Ocultar' : 'Ver'}
                </button>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className={`rounded-control border px-3 py-2 text-[13px] ${
                  locked
                    ? 'border-warn-line bg-warn-soft text-warn-text'
                    : 'border-danger-line bg-danger-soft text-danger-text'
                }`}
              >
                <p>{error.message}</p>
                {typeof error.remainingAttempts === 'number' && error.remainingAttempts > 0 && (
                  <p className="mt-1 text-[11.5px]">
                    Te {error.remainingAttempts === 1 ? 'queda' : 'quedan'}{' '}
                    {error.remainingAttempts}{' '}
                    {error.remainingAttempts === 1 ? 'intento' : 'intentos'} antes de que la cuenta
                    se bloquee.
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || locked}
              className="mt-1 w-full rounded-control bg-brand px-3 py-2.5 text-[13.5px] font-semibold text-brand-on transition-colors hover:bg-brand-strong disabled:opacity-50"
            >
              {submitting ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          <p className="mt-6 text-center text-[12.5px] text-subtle">
            Tienes un token de registro?{' '}
            <Link to="/registro" className="font-semibold text-brand-deep hover:underline">
              Crea tu cuenta
            </Link>
          </p>

          <p className="mt-3 text-center text-[11.5px] text-faint">
            Solo puede haber una sesion abierta por cuenta. Al entrar aqui se cierra cualquier otra.
          </p>
        </div>
      </main>
    </div>
  );
}
